<template>
  <CustomExtrasFields :booking="booking" :disabled="disabled">
    <template #actions>
      <CVButton
        v-if="showContractPenaltyButton"
        :disabled="
          isContractPenaltyDisabled || isAddingContractPenalty || !$can('create', 'Invoice')
        "
        data-testid="add-contract-penalty"
        @click.prevent="showContractPenaltyModal = true"
      >
        {{ t('addContractPenalty') }}
      </CVButton>
    </template>
  </CustomExtrasFields>

  <div v-if="hasChanged && !areAllExtrasReadOnly" class="col-span-3 flex">
    <CVButton variant="default" :is-loading="isBusy" class="mr-2" @click.prevent="saveCustomExtras">
      {{ t('save') }}
    </CVButton>
    <CVButton
      variant="secondary"
      :disabled="isBusy"
      class="mr-2"
      @click.prevent="cancelCustomExtras"
    >
      {{ t('cancel') }}
    </CVButton>
  </div>

  <ContractPenaltyModal
    v-model="showContractPenaltyModal"
    :booking="booking"
    :is-submitting="isAddingContractPenalty"
    @confirm="handleContractPenaltyConfirm"
  />
</template>

<script lang="ts" setup>
import { getCustomBookedExtras } from '@/utils/pricing.utils';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import { BookingStatus, ContractPenaltyType } from '@carvia/ros-client-types';
import { useAddContractPenalty } from '@/queries/use-contract-penalties';
import ContractPenaltyModal from '@/components/booking-forms/ContractPenaltyModal.vue';
import CustomExtrasFields from '@/components/booking-forms/CustomExtrasFields.vue';
import { useFieldArray, useForm } from 'vee-validate';
import { customExtrasSchema } from '@/validation/price-calculation.schema';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { cloneDeep } from 'lodash';
import CVButton from '@/components/buttons/CVButton.vue';
import type { CustomBookedExtra } from '@/entities/bookings/booked-extra';
import { PriceRecalculationMode } from '@carvia/ros-client-types';
import { areAllExtrasFrozen } from './use-is-extra-type-frozen';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';

const props = defineProps<{
  booking: Booking;
  disabled?: boolean;
  contractPenaltyDisabled?: boolean;
}>();
const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();
const { mutateAsync: addContractPenalty, isPending: isAddingContractPenalty } =
  useAddContractPenalty();

const showContractPenaltyModal = ref(false);
const { confirmAndSave, isBusy, reset } = usePriceChangeConfirmation();

const editableCustomExtras = (extras: CustomBookedExtra[] | null | undefined) =>
  (extras ?? []).filter((extra) => !extra.isAdditional);

const additionalCustomExtras = () =>
  (getCustomBookedExtras(props.booking.priceCalculation) ?? []).filter(
    (extra) => extra.isAdditional,
  );

const {
  handleSubmit,
  values: formValues,
  setValues,
} = useForm({
  initialValues: {
    customBookedExtras: editableCustomExtras(getCustomBookedExtras(props.booking.priceCalculation)),
  },
  validationSchema: customExtrasSchema,
});

const { replace } = useFieldArray<CustomBookedExtra>('customBookedExtras');

watch(
  () => getCustomBookedExtras(props.booking.priceCalculation),
  () => {
    replace(cloneDeep(editableCustomExtras(getCustomBookedExtras(props.booking.priceCalculation))));
    unchangedValues.value = cloneDeep(formValues);
  },
);

const unchangedValues = ref(cloneDeep(formValues));

const saveCustomExtras = handleSubmit(async (values) => {
  const updateBookingDto: UpdateBookingDto = {
    pricing: {
      customBookedExtras: [
        ...additionalCustomExtras(),
        ...((values.customBookedExtras as CustomBookedExtra[]) ?? []),
      ] as any, // TODO: Solve stupid null / undefined problem,
      priceRecalculationMode: PriceRecalculationMode.REBOOK,
    },
  };
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto,
    confirmOnlyWhenCancellationFee: true,
  });
  if (status === 'cancelled') {
    cancelCustomExtras();
    return;
  }
  if (status !== 'saved') {
    return;
  }
  unchangedValues.value = cloneDeep(formValues);
});

const cancelCustomExtras = () => {
  replace(editableCustomExtras(getCustomBookedExtras(props.booking.priceCalculation)));
  reset();
};

watch(
  () => formValues,
  () => {
    if (!formValues.customBookedExtras) {
      setValues({ customBookedExtras: [] });
    }
  },
  { deep: true },
);

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));

const areAllExtrasReadOnly = computed(() => areAllExtrasFrozen(props.booking.status));

const isContractPenaltyDisabled = computed(
  () => props.contractPenaltyDisabled ?? props.disabled ?? false,
);

const showContractPenaltyButton = computed(() =>
  [BookingStatus.HANDED_OVER, BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED].includes(
    props.booking.status,
  ),
);

const handleContractPenaltyConfirm = async ({
  type,
  attemptDepositCharge,
}: {
  type: ContractPenaltyType;
  attemptDepositCharge?: boolean;
}) => {
  await addContractPenalty({
    bookingId: props.booking.id,
    type,
    attemptDepositCharge,
  });

  showContractPenaltyModal.value = false;
};
</script>

<i18n lang="json">
{
  "en": {
    "addContractPenalty": "+ Add contract penalty"
  },
  "de": {
    "addContractPenalty": "+ Strafgebühr hinzufügen"
  },
  "es": {
    "addContractPenalty": "+ Añadir penalización"
  }
}
</i18n>
